import React, { useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CHEVRON_LEFT_ICON, CHEVRON_RIGHT_ICON } from '../../../../constants/images';
import { IBubbleChartCard } from '../../../../interfaces/cards';
import { IBubbleData } from '../../../../interfaces/charts';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import { TRANSLATIONS_METRICS_NE } from '../../../../translations/metrics';
import BdsIconButton from '../../Buttons/BdsIconButton/BdsIconButton';
import BubbleChart from '../../Charts/BubbleChart/BubbleChart';
import Card from '../Card/Card';
import EmptyCard from '../EmptyCard/EmptyCard';
import ErrorCard from '../ErrorCard/ErrorCard';
import BubblesChartContentExpanded from './BubblesChartContentExpanded/BubblesChartContentExpanded';

import './BubbleChartCard.scss';

const BubbleChartCard = ({
  title,
  cardData,
  chartData,
  colorScheme,
  unitsName,
  loading,
  error,
  isPercentage = false,
  action
}: IBubbleChartCard) => {
  const { t } = useTranslation();

  const [filteredData, setFilteredData] = useState<IBubbleData[]>();
  const [expandedData, setExpandedData] = useState<IBubbleData[]>();
  const [showExpandedContent, setShowExpandedContent] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const hasError = useMemo(() => {
    return error && !loading;
  }, [error, loading]);

  const isLoading = useMemo(() => {
    return loading && !error;
  }, [loading, error]);

  useEffect(() => {
    if (chartData) {
      setFilteredData(chartData.slice(0, 4));
      setExpandedData(chartData.slice(4, 8));
    }
  }, [chartData]);

  useEffect(() => {
    if (expandedData?.length) {
      setShowExpandedContent(true);
    } else {
      setShowExpandedContent(false);
    }
  }, [expandedData]);

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (hasError) {
    return (
      <Card className="bubble-chart-card-error-container" title={title}>
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={action}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          className="bubble-chart-card-error"
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="bubble-chart-card-skeleton-container">
        <Skeleton className="bubble-chart-card-skeleton" variant="rectangular" />
      </div>
    );
  }

  if (!cardData.length || !filteredData?.length) {
    return (
      <Card className="bubble-chart-card-error-container" title={title}>
        <EmptyCard
          primaryText={t(TRANSLATIONS_METRICS_NE.emptyCard.title)}
          secondaryText={t(TRANSLATIONS_METRICS_NE.emptyCard.description)}
        />
      </Card>
    );
  }
  const renderButtonExpanded = () => {
    return (
      <BdsIconButton
        variant="inverse"
        className="icon-button"
        size="lg"
        icon={isExpanded ? CHEVRON_LEFT_ICON : CHEVRON_RIGHT_ICON}
        action={handleExpand}
      />
    );
  };

  return (
    <Card className={`bubble-container flex ${isExpanded ? 'expanded-mode' : ''}`} title={title} data-experience="new">
      <div className={`bubble-chart-card-container flex flex-column ${isExpanded ? 'expanded-mode' : ''}`}>
        <div className="bubble-chart-card-text-container">
          {cardData.map((item) => (
            <div key={item.label} className="flex flex-col gap-1">
              <div className={`number ${colorScheme}`}>{item.value}</div>
              <div className="label">{item.label}</div>
            </div>
          ))}
        </div>
        <div
          className={`row flex flex-row items-center justify-start ${isExpanded ? 'expanded' : ''} length-${
            expandedData?.length
          }`}
        >
          <BubbleChart
            isExpanded={isExpanded}
            data={filteredData}
            extraData={expandedData}
            colorScheme={colorScheme}
            unitsName={unitsName}
            isPercentage={isPercentage}
          />
          {showExpandedContent && (
            <div className={`expansible-container flex items-center ${isExpanded ? 'expanded' : ''}`}>
              {renderButtonExpanded()}
            </div>
          )}
        </div>
      </div>
      {isExpanded && showExpandedContent && (
        <BubblesChartContentExpanded expandedData={expandedData} child={renderButtonExpanded()} />
      )}
    </Card>
  );
};

export default BubbleChartCard;
